import React, { useState } from "react";
import { TeamAPI } from "../../../services/api";

interface TeamMember {
  id: number;
  user_id: number;
  member_position: string;
  full_name: string;
  personal_email: string;
}

interface Team {
  id: number;
  team_id: string;
  team_name: string;
  members: TeamMember[];
  [key: string]: any;
}

interface ConfirmTeamMateStatusProps {
  team: Team;
  onUpdate: () => void;
}

const ConfirmTeamMateStatus: React.FC<ConfirmTeamMateStatusProps> = ({
  team,
  onUpdate,
}) => {
  const [loading, setLoading] = useState(false);

  const pendingMember = team.members.find((m) => m.member_position === "2");

  const handleConfirm = async () => {
    if (!pendingMember) return;

    setLoading(true);
    try {
      const response = await TeamAPI.confirmMember(
        team.team_id,
        pendingMember.id
      );
      console.log("Team member confirmed:", response);

      // Refresh team data to show updated status
      onUpdate();
    } catch (error) {
      console.error("Error confirming team member:", error);
      alert("Failed to confirm team member. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!pendingMember) return;

    setLoading(true);
    try {
      const response = await TeamAPI.rejectMember(
        team.team_id,
        pendingMember.id
      );
      console.log("Team member rejected:", response);

      // Refresh team data to show updated status
      onUpdate();
    } catch (error) {
      console.error("Error rejecting team member:", error);
      alert("Failed to reject team member. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!pendingMember) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm">
      <div className="flex items-center space-x-2 mb-4">
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M13.3333 17.5V15.8333C13.3333 14.9493 12.9821 14.1014 12.3569 13.4763C11.7318 12.8512 10.884 12.5 9.99992 12.5H4.16658C3.28253 12.5 2.43468 12.8512 1.80956 13.4763C1.18444 14.1014 0.833252 14.9493 0.833252 15.8333V17.5M14.1666 9.16667L15.8333 10.8333L19.1666 7.5M10.4166 5.83333C10.4166 7.67428 8.9242 9.16667 7.08325 9.16667C5.2423 9.16667 3.74992 7.67428 3.74992 5.83333C3.74992 3.99238 5.2423 2.5 7.08325 2.5C8.9242 2.5 10.4166 3.99238 10.4166 5.83333Z"
            stroke="url(#paint0_linear_464_3997)"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <defs>
            <linearGradient
              id="paint0_linear_464_3997"
              x1="9.99992"
              y1="2.5"
              x2="9.99992"
              y2="17.5"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#403634" />
              <stop offset="1" stopColor="#1E1E1E" />
            </linearGradient>
          </defs>
        </svg>
        <h3 className="text-lg font-semibold text-gray-900">
          Confirm your team member
        </h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Full name
          </label>
          <input
            type="text"
            value={pendingMember.full_name}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
            readOnly
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email
          </label>
          <input
            type="email"
            value={pendingMember.personal_email}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
            readOnly
          />
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={handleReject}
          disabled={loading}
          className="flex-1 bg-gray-800 text-white py-3 px-4 rounded-lg hover:bg-gray-700 transition-colors font-medium disabled:opacity-50"
        >
          {loading ? "Processing..." : "Reject"}
        </button>
        <button
          onClick={handleConfirm}
          disabled={loading}
          className="flex-1 bg-gray-300 text-gray-800 py-3 px-4 rounded-lg hover:bg-gray-200 transition-colors font-medium disabled:opacity-50"
        >
          {loading ? "Processing..." : "Confirm"}
        </button>
      </div>
    </div>
  );
};

export default ConfirmTeamMateStatus;
